import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import type { ReactNode } from "react";
import type { PageProps } from "../../pages";

export type InsuranceAdvisorData = {
  insuranceAdvisorWidgetUrl: string;
  featureHumanInTheLoop: string;
  featureConversationalInsuranceAdvisor: string;
  primaryTrade: string;
  serviceChannelIdentifier?: string;
  businessUnit?: string;
  usState?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  vertical?: string;
  rfqId?: string;
  productCode?: string;
};

export const getInsuranceAdvisorData = (
  pageData: PageProps,
): InsuranceAdvisorData | undefined =>
  pageData.scripts?.find(
    ({ metadata }) => metadata.name === "insurance-advisor-widget",
  )?.props as InsuranceAdvisorData | undefined;

type ValidatedInsuranceAdvisorData = InsuranceAdvisorData & {
  insuranceAdvisorProductCode: string;
};

export type InsuranceAdvisorAttributes =
  | {
      "data-product": string;
      "data-trade": string;
      "data-api": "v2";
      "data-feature-human-in-the-loop": string;
      "data-feature-conversational-insurance-advisor": string;
      "data-service-channel-identifier"?: string;
      "data-business-unit"?: string;
      "data-us-state"?: string;
      "data-first-name"?: string;
      "data-last-name"?: string;
      "data-email"?: string;
      "data-vertical"?: string;
      "data-rfq-id"?: string;
      "data-product-code"?: string;
    }
  | undefined;

export type AttributeOverrides = Partial<
  NonNullable<InsuranceAdvisorAttributes>
>;

type InsuranceAdvisorContextValue = {
  insuranceAdvisorData?: Partial<InsuranceAdvisorData>;
  getAdvisorAttributes: (
    productCode: string,
    overrides?: AttributeOverrides,
  ) => InsuranceAdvisorAttributes;
  loadWidget: () => void;
  cleanupWidget: () => void;
};

const InsuranceAdvisorContext =
  createContext<InsuranceAdvisorContextValue | null>(null);

const validateInsuranceAdvisorAttribute = (attribute: unknown) =>
  typeof attribute === "string" && attribute !== "";

const validateInsuranceAdvisorData = (
  widgetData: Partial<
    InsuranceAdvisorData & {
      insuranceAdvisorProductCode: string;
    }
  >,
): widgetData is ValidatedInsuranceAdvisorData => {
  if (!widgetData) return false;

  return [
    widgetData.insuranceAdvisorWidgetUrl,
    widgetData.featureHumanInTheLoop,
    widgetData.featureConversationalInsuranceAdvisor,
    widgetData.primaryTrade,
    widgetData.insuranceAdvisorProductCode,
  ].every(validateInsuranceAdvisorAttribute);
};

export interface InsuranceAdvisorProviderProps {
  children: ReactNode;
  insuranceAdvisorData?: Partial<InsuranceAdvisorData>;
}

export const InsuranceAdvisorProvider = ({
  children,
  insuranceAdvisorData,
}: InsuranceAdvisorProviderProps) => {
  const scriptLoadedRef = useRef(false);
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);

  const widgetUrl = insuranceAdvisorData?.insuranceAdvisorWidgetUrl;
  const hasValidUrl = widgetUrl && validateInsuranceAdvisorAttribute(widgetUrl);

  // Load script on provider mount when valid URL is present
  useEffect(() => {
    if (!hasValidUrl || scriptLoadedRef.current) {
      return;
    }

    const script = document.createElement("script");
    script.src = widgetUrl;
    script.type = "module";
    script.async = true;

    document.head.appendChild(script);
    scriptElementRef.current = script;
    scriptLoadedRef.current = true;

    return () => {
      if (
        scriptElementRef.current &&
        document.head.contains(scriptElementRef.current)
      ) {
        document.head.removeChild(scriptElementRef.current);
        scriptElementRef.current = null;
        scriptLoadedRef.current = false;
      }
    };
  }, [hasValidUrl, widgetUrl]);

  /** @deprecated Provider now handles script loading automatically */
  const loadWidget = useCallback(() => {
    // No-op: script loading now handled by useEffect
  }, []);

  /** @deprecated Provider now handles cleanup automatically */
  const cleanupWidget = useCallback(() => {
    // No-op: cleanup now handled by useEffect
  }, []);

  const contextValue = useMemo<InsuranceAdvisorContextValue>(() => {
    return {
      insuranceAdvisorData,
      loadWidget,
      cleanupWidget,
      getAdvisorAttributes: (
        productCode: string,
        overrides: AttributeOverrides = {},
      ) => {
        if (!insuranceAdvisorData) {
          return undefined;
        }

        const dataWithProductCode = {
          ...insuranceAdvisorData,
          insuranceAdvisorProductCode: productCode,
          // Allow overrides to satisfy validation for fields not yet
          // available at page load (e.g. primaryTrade in UK Q2Q)
          ...(overrides["data-trade"] && {
            primaryTrade: overrides["data-trade"],
          }),
        };

        const isValid = validateInsuranceAdvisorData(dataWithProductCode);

        if (!isValid) {
          return undefined;
        }

        const {
          primaryTrade,
          featureConversationalInsuranceAdvisor,
          featureHumanInTheLoop,
          serviceChannelIdentifier,
          businessUnit,
          usState,
          firstName,
          lastName,
          email,
          vertical,
          rfqId,
          productCode: dataProductCode,
          insuranceAdvisorProductCode,
        } = dataWithProductCode;

        return {
          "data-api": "v2",
          "data-trade": primaryTrade,
          "data-product": insuranceAdvisorProductCode,
          "data-feature-conversational-insurance-advisor":
            featureConversationalInsuranceAdvisor,
          "data-feature-human-in-the-loop": featureHumanInTheLoop,
          ...(serviceChannelIdentifier && {
            "data-service-channel-identifier": serviceChannelIdentifier,
          }),
          ...(businessUnit && {
            "data-business-unit": businessUnit,
          }),
          ...(usState && {
            "data-us-state": usState,
          }),
          ...(firstName && {
            "data-first-name": firstName,
          }),
          ...(lastName && {
            "data-last-name": lastName,
          }),
          ...(email && {
            "data-email": email,
          }),
          ...(vertical && {
            "data-vertical": vertical,
          }),
          ...(rfqId && {
            "data-rfq-id": rfqId,
          }),
          ...(dataProductCode && {
            "data-product-code": dataProductCode,
          }),
          ...overrides,
        };
      },
    };
  }, [insuranceAdvisorData, loadWidget, cleanupWidget]);

  return (
    <InsuranceAdvisorContext.Provider value={contextValue}>
      {children}
      {hasValidUrl && (
        <div
          id="insurance-advisor-widget"
          data-props="insuranceAdvisorProps"
          data-testid="insuranceAdvisorWidget"
        />
      )}
    </InsuranceAdvisorContext.Provider>
  );
};

export const useInsuranceAdvisor = () => {
  const context = useContext(InsuranceAdvisorContext);

  if (!context) {
    throw new Error(
      "useInsuranceAdvisor must be used within InsuranceAdvisorProvider",
    );
  }

  return context;
};
